import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  Search, Clock, Users, MapPin,
  Eye, ArrowRight, Star, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { GET_ALL_TOURS } from '../graphql/tours';
import { TRACK_SEARCH } from '../graphql/analytics';
import { getVisitorSessionId } from '../lib/visitorSession';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

const PAGE_SIZE = { grid: 9, list: 6 };

/* ─── Quick filters ─── */
const DURATION_FILTERS = [
  { value: 'all',    label: 'Any Length' },
  { value: 'short',  label: 'Up to 6 Days',              max: 6 },
  { value: 'medium', label: '7–10 Days',    min: 7,       max: 10 },
  { value: 'long',   label: '11+ Days',     min: 11 },
];

const PRICE_TIERS = [
  { value: 'all',    label: 'Any Budget' },
  { value: 'budget', label: 'Budget',    sub: 'Under $2,500',   max: 2500 },
  { value: 'mid',    label: 'Mid-Range', sub: '$2,500–$4,500',  min: 2500, max: 4500 },
  { value: 'luxury', label: 'Luxury',    sub: '$4,500+',        min: 4500 },
];

const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');
    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }
    .hero-grain {
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
    }
    .tour-card {
      position: relative; background: #fff; border: 1px solid #e8e4de;
      transition: box-shadow 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1);
      overflow: hidden;
    }
    .tour-card:hover { box-shadow: 0 24px 80px -12px rgba(0,0,0,0.13); transform: translateY(-3px); }
    .tour-card-img img { transition: transform 0.8s cubic-bezier(0.22,1,0.36,1); }
    .tour-card:hover .tour-card-img img { transform: scale(1.06); }
    .cat-pill {
      position: relative; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase; padding: 8px 18px; border: 1px solid #e2ddd6;
      background: transparent; color:#12100e; transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
      white-space: nowrap; cursor: pointer;
    }
    .cat-pill:hover { color: #334155; border-color: #cbd5e1; }
    .cat-pill.active { color: ${NAVY}; border-color: ${GOLD}; background: ${GOLD_DIM}; }
    .pill-sub {
      margin-left: 7px; font-weight: 400; letter-spacing: 0.04em;
      text-transform: none; opacity: 0.55; font-size: 11px;
    }
    .filter-group-label {
      font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 600;
      letter-spacing: 0.18em; text-transform: uppercase; color: #a8a196;
      margin-right: 2px; white-space: nowrap;
    }
    .search-wrap { position: relative; }
    .search-wrap input {
      font-family: 'Jost', sans-serif; font-size:15px; letter-spacing: 0.05em;
      background: #f7f5f2; border: 1px solid #e8e4de; color: #0f172a;
      padding: 12px 16px 12px 44px; width: 100%; transition: border-color 0.3s, background 0.3s;
      outline: none;
    }
    .search-wrap input:focus { border-color: ${GOLD}; background: #fff; }
    .view-btn {
      width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
      border: 1px solid #e8e4de; color:#12100e; transition: all 0.25s; cursor: pointer; background: transparent;
    }
    .view-btn.active { border-color: ${GOLD}; color: ${NAVY}; background: ${GOLD_DIM}; }
    .stat-item + .stat-item { border-left: 1px solid #e8e4de; padding-left: 16px; }
    .reserve-btn {
      position: relative; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase; padding: 12px 24px;
      border: 1px solid ${NAVY}; background: ${NAVY}; color: #fff;
      display: inline-flex; align-items: center; gap: 8px; overflow: hidden;
      transition: all 0.35s cubic-bezier(0.22,1,0.36,1); white-space: nowrap;
    }
    .reserve-btn::before {
      content: ''; position: absolute; inset: 0; background: ${GOLD};
      transform: scaleX(0); transform-origin: left;
      transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .reserve-btn:hover::before { transform: scaleX(1); }
    .reserve-btn:hover { border-color: ${GOLD}; color: ${NAVY}; }
    .reserve-btn span, .reserve-btn svg { position: relative; z-index: 1; }
    .detail-btn {
      width: 42px; height: 42px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid #e8e4de; color:#12100e; transition: all 0.3s;
    }
    .detail-btn:hover { border-color: ${GOLD}; color: ${NAVY}; }
    .tag-badge {
      font-family: 'Jost', sans-serif; font-size:12px; font-weight: 600; letter-spacing:0.16em;
      text-transform: uppercase; padding: 5px 12px; background: rgba(8,14,26,0.75);
      backdrop-filter: blur(8px); color: #e2c98a; border: 1px solid rgba(184,151,90,0.35);
    }
    @keyframes cardReveal {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .card-reveal { animation: cardReveal 0.5s cubic-bezier(0.22,1,0.36,1) both; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.15) 100%); }
    .list-img { min-width: 280px; max-width: 280px; }

    /* ── Pagination ── */
    .pg-btn {
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.14em; min-width: 36px; height: 36px;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1px solid #e8e4de; background: #fff; color:#12100e;
      transition: all 0.25s; cursor: pointer; padding: 0 10px;
    }
    .pg-btn:hover:not(:disabled) { border-color: ${GOLD}; color: ${NAVY}; }
    .pg-btn.active { border-color: ${GOLD}; background: ${GOLD}; color: ${NAVY}; }
    .pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .pg-ellipsis {
      font-family: 'Jost', sans-serif; font-size:15px; color:#12100e;
      width: 28px; text-align: center; line-height: 36px; user-select: none;
    }
  `}</style>
);

/* ─── Helpers ─── */
const RatingStars = ({ rating }) => (
  <div className="flex items-center gap-1">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={10}
        fill={i <= Math.round(rating) ? GOLD : 'none'}
        stroke={i <= Math.round(rating) ? GOLD : '#cbd5e1'}
        strokeWidth={1.5} />
    ))}
  </div>
);

/* ─── Pagination component ─── */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 1;

  const range = (from, to) => {
    const arr = [];
    for (let i = from; i <= to; i++) arr.push(i);
    return arr;
  };

  const left  = Math.max(2, currentPage - delta);
  const right = Math.min(totalPages - 1, currentPage + delta);

  pages.push(1);
  if (left > 2)                pages.push('…left');
  pages.push(...range(left, right));
  if (right < totalPages - 1)  pages.push('…right');
  if (totalPages > 1)          pages.push(totalPages);

  return (
    <div className="flex items-center justify-center gap-1.5 mt-14">
      <button
        className="pg-btn"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronLeft size={13} strokeWidth={1.5} />
      </button>

      {pages.map((p) =>
        typeof p === 'string' ? (
          <span key={p} className="pg-ellipsis">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`pg-btn ${p === currentPage ? 'active' : ''}`}
          >
            {p}
          </button>
        )
      )}

      <button
        className="pg-btn"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRight size={13} strokeWidth={1.5} />
      </button>
    </div>
  );
};

/* ─── Grid Card ─── */
const GridCard = ({ tour, index }) => (
  <div className="tour-card card-reveal flex flex-col" style={{ animationDelay: `${index * 80}ms` }}>
    {/* Image */}
    <Link to={`/tours/${tour.slug}`} className="tour-card-img relative h-64 overflow-hidden block flex-shrink-0">
      <img src={tour.featuredImage} alt={tour.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      <div className="absolute top-4 left-4">
        <span className="tag-badge">{tour.category?.name || 'Safari'}</span>
      </div>
      <div className="absolute bottom-4 left-4 flex items-center gap-1.5">
        <MapPin size={11} strokeWidth={1.5} style={{ color: GOLD }} />
        <span className="f-jost text-[13px] text-white/90 tracking-wide font-light">
          {tour.parks?.[0]?.country?.name || 'Global'}
        </span>
      </div>
    </Link>

    {/* Body */}
    <div className="flex flex-col flex-1 p-6 min-w-0">
      <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-3 truncate" style={{ color: GOLD }}>
        {tour.category?.name}
      </span>

      <Link to={`/tours/${tour.slug}`} style={{ textDecoration: 'none' }}>
        <h3 className="f-cormorant text-[27px] font-light text-slate-900 leading-snug mb-4 hover:opacity-70 transition-opacity break-words hyphens-auto">
          {tour.name}
        </h3>
      </Link>

      <p className="f-jost text-[15px] text-slate-500 leading-relaxed mb-5 font-light flex-1 line-clamp-2 break-words">
        {tour.tagline}
      </p>

      {/* Stats row — wraps on xs instead of compressing */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5">
        <div className="flex items-center gap-1.5">
          <Clock size={11} style={{ color: GOLD }} />
          <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">{tour.durationLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users size={11} style={{ color: GOLD }} />
          <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">Max {tour.maxParticipants}</span>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <RatingStars rating={tour.averageRating} />
          <span className="f-jost text-[13px] text-slate-400 whitespace-nowrap">({tour.totalReviews})</span>
        </div>
      </div>

      <div className="gold-rule mb-5" />

      {/* Footer — wraps price above buttons on very narrow cards */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 block mb-0.5">From</span>
          <div className="flex items-baseline gap-1">
            <span className="f-cormorant text-[34px] font-light text-slate-900 leading-none">
              ${Math.round(tour.currentPrice || 0).toLocaleString()}
            </span>
            <span className="f-jost text-[13px] text-slate-400">/ Person</span>
          </div>
        </div>
        {/* Buttons always stay together and never compress */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to={`/tours/${tour.slug}`} className="detail-btn" title="View expedition details">
            <Eye size={15} />
          </Link>
          <Link to={`/booking/${tour.slug}`} className="reserve-btn">
            <span>Reserve</span>
            <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </div>
  </div>
);

/* ─── List Card ─── */
const ListCard = ({ tour, index }) => (
  <div className="tour-card card-reveal flex flex-col md:flex-row" style={{ animationDelay: `${index * 60}ms` }}>
    <Link to={`/tours/${tour.slug}`} className="tour-card-img list-img relative h-56 md:h-auto overflow-hidden block flex-shrink-0">
      <img src={tour.featuredImage} alt={tour.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
      <div className="absolute top-4 left-4">
        <span className="tag-badge">{tour.category?.name}</span>
      </div>
    </Link>

    <div className="flex flex-col flex-1 p-6 sm:p-8 justify-between min-w-0">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
            {tour.category?.name}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <RatingStars rating={tour.averageRating} />
            <span className="f-jost text-[13px] text-slate-400">({tour.totalReviews})</span>
          </div>
        </div>

        <Link to={`/tours/${tour.slug}`} style={{ textDecoration: 'none' }}>
          <h3 className="f-cormorant text-[29px] sm:text-[30px] font-light text-slate-900 leading-snug mb-2 hover:opacity-70 transition-opacity break-words hyphens-auto">
            {tour.name}
          </h3>
        </Link>

        <div className="flex items-center gap-1.5 mb-4">
          <MapPin size={11} style={{ color: GOLD }} />
          <span className="f-jost text-[13px] text-slate-500 tracking-wide">
            {tour.parks?.[0]?.country?.name}
          </span>
        </div>

        <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed max-w-lg break-words">
          {tour.tagline}
        </p>
      </div>

      {/* Footer — stacks on mobile, row on sm+ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6">
        {/* Duration + group size */}
        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Clock size={11} style={{ color: GOLD }} />
            <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">{tour.durationLabel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users size={11} style={{ color: GOLD }} />
            <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">Max {tour.maxParticipants}</span>
          </div>
        </div>

        {/* Price + buttons — flex-wrap keeps them from squeezing together */}
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <span className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 block mb-0.5">From</span>
            <div className="flex items-baseline gap-1">
              <span className="f-cormorant text-[32px] sm:text-[34px] font-light text-slate-900 leading-none">
                ${Math.round(tour.currentPrice || 0).toLocaleString()}
              </span>
              <span className="f-jost text-[13px] text-slate-400">/person</span>
            </div>
          </div>
          {/* Vertical divider only visible when on same row and not wrapped */}
          <div className="w-px h-10 bg-slate-100 hidden sm:block" />
          {/* Buttons stay together, never compress */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to={`/tours/${tour.slug}`} className="detail-btn" title="View expedition details">
              <Eye size={15} />
            </Link>
            <Link to={`/booking/${tour.slug}`} className="reserve-btn">
              <span>Reserve</span>
              <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Skeleton loader ─── */
const SkeletonCard = () => (
  <div style={{ background: '#fff', border: '1px solid #e8e4de', overflow: 'hidden' }}>
    <div style={{ height: '256px', background: 'linear-gradient(90deg,#f0ece6 25%,#e8e4de 50%,#f0ece6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
    <div style={{ padding: '28px' }}>
      {[80, 60, 100, 40].map((w, i) => (
        <div key={i} style={{ height: '12px', width: `${w}%`, background: '#f0ece6', marginBottom: '14px', borderRadius: '2px' }} />
      ))}
    </div>
    <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
  </div>
);

/* ─── Main Page ─── */
const ToursPage = () => {
  const [searchTerm,        setSearchTerm]        = useState('');
  const [debouncedSearch,   setDebouncedSearch]   = useState('');
  const [selectedCategory,  setSelectedCategory]  = useState('all');
  const [selectedDuration,  setSelectedDuration]  = useState('all');
  const [selectedPriceTier, setSelectedPriceTier] = useState('all');
  const [viewMode,          setViewMode]          = useState('grid');
  const [currentPage,       setCurrentPage]       = useState(1);
  const catRef     = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedDuration, selectedPriceTier, viewMode]);

  const pageSize = PAGE_SIZE[viewMode];
  const offset   = (currentPage - 1) * pageSize;

  const activeDuration  = DURATION_FILTERS.find(d => d.value === selectedDuration);
  const activePriceTier = PRICE_TIERS.find(p => p.value === selectedPriceTier);

  const { data, loading, previousData } = useQuery(GET_ALL_TOURS, {
    variables: {
      limit:           pageSize,
      offset,
      categorySlug:    selectedCategory !== 'all' ? selectedCategory : undefined,
      search:          debouncedSearch  || undefined,
      minDurationDays: activeDuration?.min,
      maxDurationDays: activeDuration?.max,
      minPrice:        activePriceTier?.min,
      maxPrice:        activePriceTier?.max,
    },
    fetchPolicy: 'cache-and-network',
  });

  const activeData = data || previousData;
  const tours      = activeData?.tours      || [];
  const totalCount = activeData?.toursCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  /* ── Track real searches (debounced, once results have settled) ── */
  const [trackSearch] = useMutation(TRACK_SEARCH);
  const lastTrackedSearch = useRef('');
  useEffect(() => {
    if (!debouncedSearch || loading) return;
    if (lastTrackedSearch.current === debouncedSearch) return;
    lastTrackedSearch.current = debouncedSearch;
    trackSearch({
      variables: {
        query:        debouncedSearch,
        searchType:   'tours',
        sessionId:    getVisitorSessionId(),
        resultsCount: totalCount,
      },
    }).catch(() => {});
  }, [debouncedSearch, loading, totalCount, trackSearch]);

  const { data: allData } = useQuery(GET_ALL_TOURS, {
    variables: { limit: 200, offset: 0 },
    fetchPolicy: 'cache-first',
  });
  const allTours = allData?.tours || [];
  const categories = [
    { value: 'all', label: 'All Safaris' },
    ...Array.from(
      new Map(allTours.map(t => [t.category?.slug, t.category])).values()
    )
    .filter(Boolean)
    .map(cat => ({ value: cat.slug, label: cat.name })),
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen f-jost" style={{ background: '#F5F3EF' }}>
      <PageStyles />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden dark-sec" style={{ background: NAVY, paddingTop: '7rem', paddingBottom: '5rem' }}>
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1761078206756-68d3023f3021?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(8,14,26,0.62) 40%, rgba(8,14,26,0.22) 100%)' }} />
        </div>
        <div className="absolute inset-0 hero-grain opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 relative z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Safaris & Tours</span>
            </div>
            <h1 className="f-cormorant text-white font-light leading-[0.95] mb-6" style={{ fontSize: 'clamp(52px,6vw,80px)', textShadow: '0 2px 20px rgba(0,0,0,0.55), 0 0 60px rgba(0,0,0,0.3)' }}>
              Our Safari<br />Collection
            </h1>
            <p className="f-jost text-slate-400 text-[16px] font-light leading-relaxed max-w-md" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
              Private, guided safaris across East Africa — book one as it is, or let us tailor it to your dates.
            </p>
            <div className="flex items-center gap-3 mt-8">
              <span className="f-cormorant text-white/50 text-lg">{totalCount} {totalCount === 1 ? 'safari' : 'safaris'}</span>
              <div className="w-8 h-px bg-white/20" />
              <span className="f-jost text-[13px] uppercase tracking-[0.16em] text-white/40">{new Date().getFullYear()} season</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sticky Toolbar ── */}
      <div className="sticky z-50 border-b" style={{ top: 0, background: 'rgba(245,243,239,0.95)', backdropFilter: 'blur(16px)', borderColor: '#e2ddd6' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 py-4">
            <div className="search-wrap flex-1 max-w-sm relative">
              <Search size={14} strokeWidth={1.5}
                className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10"
                style={{ color: '#12100e' }} />
              <input type="text" placeholder="Search by name or destination…"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <span className="f-jost text-[13px] text-slate-400 tracking-wide hidden sm:block">
                {loading ? '—' : `${totalCount} ${totalCount === 1 ? 'result' : 'results'}`}
              </span>
              <div className="w-px h-5 bg-slate-200 hidden sm:block" />
              <div className="flex gap-1">
                <button onClick={() => setViewMode('grid')} className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <rect x="0" y="0" width="6" height="6" stroke="currentColor" strokeWidth="1.2"/>
                    <rect x="8" y="0" width="6" height="6" stroke="currentColor" strokeWidth="1.2"/>
                    <rect x="0" y="8" width="6" height="6" stroke="currentColor" strokeWidth="1.2"/>
                    <rect x="8" y="8" width="6" height="6" stroke="currentColor" strokeWidth="1.2"/>
                  </svg>
                </button>
                <button onClick={() => setViewMode('list')} className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <line x1="0" y1="2"  x2="14" y2="2"  stroke="currentColor" strokeWidth="1.2"/>
                    <line x1="0" y1="7"  x2="14" y2="7"  stroke="currentColor" strokeWidth="1.2"/>
                    <line x1="0" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="1.2"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div ref={catRef} className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-4">
            {categories.map(cat => (
              <button key={cat.value} onClick={() => setSelectedCategory(cat.value)}
                className={`cat-pill ${selectedCategory === cat.value ? 'active' : ''}`}>
                {cat.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-7 gap-y-3 pb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="filter-group-label">Duration</span>
              {DURATION_FILTERS.map(d => (
                <button key={d.value} onClick={() => setSelectedDuration(d.value)}
                  className={`cat-pill ${selectedDuration === d.value ? 'active' : ''}`}>
                  {d.label}
                </button>
              ))}
            </div>
            <div className="w-px h-6 bg-slate-200 hidden sm:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="filter-group-label">Budget</span>
              {PRICE_TIERS.map(p => (
                <button key={p.value} onClick={() => setSelectedPriceTier(p.value)}
                  className={`cat-pill ${selectedPriceTier === p.value ? 'active' : ''}`}>
                  {p.label}
                  {p.sub && <span className="pill-sub">{p.sub}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-12">
        <div ref={resultsRef} className="flex items-center gap-4 mb-8">
          <div className="w-6 h-px" style={{ background: GOLD }} />
          <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            {selectedCategory === 'all' ? 'All Safaris' : selectedCategory}
          </span>
          <div className="flex-1 h-px bg-slate-200" />
          <span className="f-cormorant text-slate-400 text-[18px]">
            {loading ? '…' : `${totalCount} journeys`}
          </span>
        </div>

        {loading && !previousData ? (
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7'
            : 'flex flex-col gap-5'}>
            {Array.from({ length: pageSize }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : tours.length > 0 ? (
          <div
            className={viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7'
              : 'flex flex-col gap-5'}
            style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}
          >
            {tours.map((tour, i) =>
              viewMode === 'grid'
                ? <GridCard key={tour.id} tour={tour} index={i} />
                : <ListCard key={tour.id} tour={tour} index={i} />
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-28 border bg-white" style={{ borderColor: '#e2ddd6' }}>
            <div className="w-10 h-px mb-6" style={{ background: GOLD }} />
            <h3 className="f-cormorant text-[32px] text-slate-800 font-light mb-2">No safaris match those filters</h3>
            <p className="f-jost text-[15px] text-slate-400 font-light mb-6">Try a different search term or category.</p>
            <button onClick={() => { setSearchTerm(''); setSelectedCategory('all'); setSelectedDuration('all'); setSelectedPriceTier('all'); }} className="reserve-btn">
              <span>Clear Filters</span>
              <ArrowRight size={11} />
            </button>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {totalPages > 1 && (
          <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 text-center mt-4">
            Page {currentPage} of {totalPages}
          </p>
        )}
      </main>
    </div>
  );
};

export default ToursPage;